import { useEffect, useMemo, useState } from "react";
import CustomButton from "../../../../../components/custom/button";
import CustomInput from "../../../../../components/custom/input";
import BookmarkAddedIcon from "@mui/icons-material/BookmarkAdded";
import { AddressValidation } from "./AddressValidation";
import { showSnackbar } from "../../../../../../app/slices/common/customSnackBar/customSnackbarSlice";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../app/redux/hooks";
import { AddressValues } from "../ProfilePageTypes";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { GoogleMapSelector } from "./GoogleMapSelector";
import {
  addAddressAsync,
  getAddressDetailsAsync,
  getAddressListAsync,
} from "../../../../../../app/slices/addressSlice/ProfileSlice";
import AddOrEditAddressSkeleton from "../../skelitons/AddOrEditAddressSkeleton";
import SearchAutoComplete from "./LocationSearchAutocomplete";
import { setCheckoutAddressId } from "../../../../../../app/slices/common/locationSlice/locationSlice";

//const MAX_LONG_DISTANCE_RADIUS_KM = 5;

export const AddOrEditAddress = ({
  modalClose,
  modalType,
  addressId,
  isLongDistance = false,
}: any) => {
  const dispatch = useAppDispatch();
  const initialState: AddressValues = {
    // completeAddress: "",
    // locality: "",
    address_type: "other",
    landmark: "",
    flat_no: "",
    // is_default: false,
  };
  const { addAddressloader, addressDetailLoader } = useAppSelector(
    (state) => state.AddressState,
  );
  const locationState = useAppSelector((state: any) => state.locationState);
  const [selectedLocation, setSelectedLocation] = useState<
    google.maps.LatLngLiteral | undefined
  >(undefined);
  const [selectedLocationOnEdit, setSelectedLocationOnEdit] = useState<
    google.maps.LatLngLiteral | undefined
  >(undefined);
  const [selectedActualAddress, setSelectedActualAddress] = useState("");
  const [selectedPincode, setSelectedPincode] = useState("");
  const [addressData, setAddressData] = useState(initialState);

  const [errors, setErrors] = useState<Partial<AddressValues>>({});

  const getAddressParts = (value: string) => {
    const cleaned = value?.trim();
    if (!cleaned) {
      return ["", ""];
    }

    const parts = cleaned
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length <= 1) {
      return [cleaned, ""];
    }

    const splitIndex = Math.min(2, parts.length - 1);
    const firstPart = parts.slice(0, splitIndex).join(", ");
    const secondPart = parts.slice(splitIndex).join(", ");

    return [firstPart, secondPart];
  };

  const currentLat = Number(
    locationState?.latitude ?? locationState?.locationGot?.latitude ?? 0,
  );
  const currentLng = Number(
    locationState?.longitude ?? locationState?.locationGot?.longitude ?? 0,
  );

  const distanceKm = useMemo(() => {
    if (!selectedLocation || !currentLat || !currentLng) {
      return null;
    }

    const toRadians = (value: number) => (value * Math.PI) / 180;
    const earthRadiusKm = 6371;
    const latDifference = toRadians(selectedLocation.lat - currentLat);
    const lngDifference = toRadians(selectedLocation.lng - currentLng);

    const a =
      Math.sin(latDifference / 2) * Math.sin(latDifference / 2) +
      Math.cos(toRadians(currentLat)) *
        Math.cos(toRadians(selectedLocation.lat)) *
        Math.sin(lngDifference / 2) *
        Math.sin(lngDifference / 2);

    const distanceKm = 2 * earthRadiusKm * Math.asin(Math.sqrt(a));

    return 2 * earthRadiusKm * Math.asin(Math.sqrt(a));
  }, [selectedLocation, currentLat, currentLng]);

  const distanceText = useMemo(() => {
    if (distanceKm === null) {
      return "";
    }

    return `${distanceKm.toFixed(1)} km`;
  }, [distanceKm]);

  const [deliveryAreaLineOne, deliveryAreaLineTwo] = useMemo(
    () => getAddressParts(selectedActualAddress),
    [selectedActualAddress],
  );

  // const [buttonConfirm, setButtonConfirm] = useState("confirm");
  // Fetch address details when in edit mode
  useEffect(() => {
    if (modalType === "edit") {
      dispatch(getAddressDetailsAsync(addressId)).then((response: any) => {
        const address = response?.payload?.data?.data;

        if (address) {
          setAddressData({
            address_type: address.address_type || "home",
            landmark: address.landmark || "",
            flat_no: address.flat_no || "",
            // is_default: address.is_default || false,
          });
          setSelectedLocation({
            lat: Number(address.latitude),
            lng: Number(address.longitude),
          });
          setSelectedLocationOnEdit({
            lat: Number(address.latitude),
            lng: Number(address.longitude),
          });
          // setSelectedActualAddress(address.completeAddress || "");
        }
      });
    }
  }, [addressId, dispatch]);
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    let curErrors: any = { ...errors };
    delete curErrors["address_type"];
    setErrors(curErrors);
    setAddressData({
      ...addressData,
      address_type: value,
    });
  };
  const inputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    let curErrors: any = { ...errors };
    delete curErrors[name];
    setErrors(curErrors);
    setAddressData({
      ...addressData,
      [name]: value,
    });
  };
  const saveDetails = () => {
    const errorCheck = AddressValidation(addressData);
    if (Object.keys(errorCheck).length === 0) {
      const deliveryArea = selectedActualAddress?.trim();
      const pincode =
        selectedPincode || deliveryArea.match(/\b\d{6}\b/)?.[0] || "";

      let payload: any = {
        ...addressData,
        area: deliveryArea,
        pincode,
        latitude: selectedLocation?.lat?.toFixed(4),
        longitude: selectedLocation?.lng?.toFixed(4),
        // is_default: addressData?.is_default,
      };

      dispatch(addAddressAsync({ payload, modalType, addressId }))
        .then((response: any) => {
          if (
            response?.payload?.status === 200 ||
            response?.payload?.status === 201
          ) {
            //dispatch(setCheckoutAddressId(response.payload.data.data.id));
            dispatch(
              showSnackbar({
                message: `Address ${
                  modalType === "edit" ? "updated" : "added"
                } successfuly.`,
                severity: "success",
                autoHide: 2000,
              }),
            );
            setAddressData(initialState);
            modalClose();
            dispatch(getAddressListAsync());
          } else {
            dispatch(
              showSnackbar({
                message: "Something went wrong, Please try again later.",
                severity: "error",
                autoHide: 2000,
              }),
            );
          }
        })
        .catch((err) => {
          dispatch(
            showSnackbar({
              message: "Something went wrong, Please try again later.",
              severity: "error",
              autoHide: 2000,
            }),
          );
        });
      // dispatch(
      //   showSnackbar({
      //     message: "validation done",
      //     severity: "success",
      //     autoHide: 2000,
      //   })
      // );
    } else {
      setErrors(errorCheck);
    }
  };
  return (
    <>
      {modalType === "edit" && addressDetailLoader ? (
        <AddOrEditAddressSkeleton />
      ) : (
        <>
          <GoogleMapSelector
            setSelectedLocation={setSelectedLocation}
            setSelectedLocationOnEdit={setSelectedLocationOnEdit}
            selectedLocation={selectedLocation}
            selectedLocationOnEdit={selectedLocationOnEdit}
            setSelectedActualAddress={setSelectedActualAddress}
            setSelectedPincode={setSelectedPincode}
            modalType={modalType}
          />
          {/* {selectedLocation && ( */}
          <div style={{ marginTop: "20px" }}>
            <div className="row">
              {/* <div className="col-md-12">
                  <CustomInput
                    label="DELIVERY AREA"
                    fullWidth
                    value={selectedActualAddress}
                  />
                </div> */}

              <div className="col-md-12 mt-3">
                <SearchAutoComplete
                  selectedActualAddress={selectedActualAddress}
                  setSelectedActualAddress={setSelectedActualAddress}
                  setSelectedLocation={setSelectedLocation}
                  setSelectedPincode={setSelectedPincode}
                />
              </div>

              {distanceText && (
                <div className="col-md-12 mt-2">
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#2d6a4f",
                      fontWeight: 600,
                      lineHeight: 1.5,
                    }}
                  >
                    `This is ${distanceText} away from your current location.`
                  </div>
                </div>
              )}

              {(selectedActualAddress ||
                deliveryAreaLineOne ||
                deliveryAreaLineTwo) && (
                <>
                  <div className="col-md-12 mt-3">
                    <CustomInput
                      label={!isLongDistance ? "" : "Delivery Area "}
                      value={deliveryAreaLineOne}
                      fullWidth
                      readOnly
                    />
                  </div>
                  <div className="col-md-12 mt-3">
                    <CustomInput
                      label=""
                      value={deliveryAreaLineTwo}
                      fullWidth
                      readOnly
                    />
                  </div>
                </>
              )}

              {/* {buttonConfirm === "save" ? ( */}
              <>
                <div className="col-md-12 mt-3">
                  <CustomInput
                    onChange={inputChange}
                    name="flat_no"
                    label="Door/Flat No"
                    value={addressData?.flat_no}
                    fullWidth
                    error={!!errors?.flat_no}
                    helperText={errors?.flat_no}
                  />
                </div>
                <div className="col-md-12 mt-3">
                  <CustomInput
                    name="landmark"
                    label="Landmark"
                    value={addressData?.landmark}
                    onChange={inputChange}
                    fullWidth
                    error={!!errors?.landmark}
                    helperText={errors?.landmark}
                  />
                </div>
                {/* <div className="col-md-12 mt-3">
                  <CustomInput
                    label="Area/Sector/Locality"
                    name="locality"
                    fullWidth
                    onChange={inputChange}
                    variant="outlined"
                    required
                    error={!!errors?.locality}
                    helperText={errors?.locality}
                  />
                </div>
                <div className="col-md-12 mt-3">
                  <CustomInput
                    label="Complete Address"
                    fullWidth
                    name="completeAddress"
                    onChange={inputChange}
                    variant="outlined"
                    required
                    multiline
                    rows={3}
                    error={!!errors?.completeAddress}
                    helperText={errors?.completeAddress}
                  />
                </div> */}
                {/* <div className="col-md-12 mt-3">
                  <FormControlLabel
                    control={<Checkbox size="small" />}
                    label="Would you like to set this as your default address?"
                  />
                </div> */}
                <div
                  className="col-md-12 mt-3"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <FormControl>
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="row-radio-buttons-group"
                      value={addressData?.address_type}
                      onChange={handleRadioChange}
                    >
                      <FormControlLabel
                        value="home"
                        control={<Radio />}
                        label="Home"
                      />
                      <FormControlLabel
                        value="work"
                        control={<Radio />}
                        label="Work"
                      />
                      <FormControlLabel
                        value="other"
                        control={<Radio />}
                        label="Other"
                      />
                    </RadioGroup>
                  </FormControl>
                </div>
              </>
              {/* ) : (
              ""
            )} */}
              {/* <div className="col-md-6 mt-2"></div> */}
              <div className="col-md-12 mt-2">
                {/* {buttonConfirm === "confirm" ? (
                <CustomButton
                  endIcon={<ArrowForwardIcon />}
                  size="large"
                  name="Confirm and Proceed"
                  fullWidth
                  onClick={() => setButtonConfirm("save")}
                />
              ) : ( */}
                <CustomButton
                  endIcon={<BookmarkAddedIcon />}
                  size="large"
                  name="Save address and proceed"
                  fullWidth
                  loader={addAddressloader}
                  onClick={saveDetails}
                />
                {/* )} */}
              </div>
            </div>
          </div>
          {/* )} */}
        </>
      )}
    </>
  );
};
